import { useEffect, useState } from 'react';
import type { SearchTenantsInput, Tenant } from '@etiquetachef/types';
import { searchTenantsCallable } from '@/lib/callables';

const TAG = 'admin/tenants/search';

export type TenantsSearchState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; items: Tenant[]; total: number }
  | { kind: 'ERROR'; error: unknown };

/**
 * Busca server-side da lista de Empresas pela callable `searchTenants`.
 *
 * Origem: #71 — 2026-06-21 18:35 +0200 (PropertyFilter server-side, Opção B).
 *
 * Por quê: filtrar e paginar no cliente exigia baixar todos os tenants; a
 * callable traduz os tokens do PropertyFilter em query Firestore e devolve só
 * a página. O PropertyFilter só dispara `onChange` ao adicionar/remover token
 * (não por tecla), então não há debounce: cada mudança de filtro/página
 * re-chama a callable.
 *
 * Contrato: `LOADING` → `READY` (`items` da página + `total`) | `ERROR`.
 * `refreshNonce` força re-fetch (botão "Atualizar"), já que a busca não é um
 * `onSnapshot`. Resposta fora de ordem é descartada pelo `cancelled` do
 * efeito (só a última busca escreve no estado).
 *
 * Usado por: `routes/TenantsPage.tsx`.
 *
 * Dados: input `SearchTenantsInput` (`tokens`, `operation`, `page`,
 * `pageSize`) e saída `SearchTenantsResult` — schemas em
 * `@etiquetachef/types`; o `propertyKey` dos tokens é o de
 * `useTenantFilter` (`tenantFilter.ts`).
 *
 * Falhas conhecidas: o `catch` só faz `console.error` — sem report para a
 * telemetria (CLAUDE.md "Observabilidade"; ver #926, achados).
 *
 * Issues: #71 (busca server-side).
 */
export function useTenantsSearch(
  input: SearchTenantsInput,
  refreshNonce: number,
): TenantsSearchState {
  const [state, setState] = useState<TenantsSearchState>({ kind: 'LOADING' });
  // Serializa o input p/ dep estável do efeito (objeto muda de identidade a
  // cada render). A string cobre tokens + page + pageSize.
  const key = JSON.stringify(input);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'LOADING' });
    searchTenantsCallable(input)
      .then((r) => {
        if (!cancelled) setState({ kind: 'READY', items: r.items, total: r.total });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error(`${TAG} failed`, err);
        setState({ kind: 'ERROR', error: err });
      });
    return (): void => {
      cancelled = true;
    };
    // `key` cobre `input`; incluí-lo direto re-dispararia a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, refreshNonce]);

  return state;
}
